import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  LinearProgress,
  TextField,
  MenuItem,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CameraAltIcon from '@mui/icons-material/CameraAlt';

// URL del backend
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function ImageUpload() {
  const { token } = useParams();
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [status, setStatus] = useState('idle'); // idle, uploading, success, error
  const [message, setMessage] = useState('');
  const [tokenValid, setTokenValid] = useState(null); // null = checking, true = valid, false = invalid
  const [procedimiento, setProcedimiento] = useState('');
  const [descripcion, setDescripcion] = useState('');

  // Opciones de procedimientos comunes
  const procedimientos = [
    { value: 'mastopexia', label: 'Mastopexia (Levantamiento de senos)' },
    { value: 'mamoplastia_aumento', label: 'Mamoplastia de aumento' },
    { value: 'mamoplastia_reduccion', label: 'Mamoplastia de reducción' },
    { value: 'liposuccion', label: 'Liposucción' },
    { value: 'abdominoplastia', label: 'Abdominoplastia' },
    { value: 'rinoplastia', label: 'Rinoplastia' },
    { value: 'blefaroplastia', label: 'Blefaroplastia (Párpados)' },
    { value: 'lifting_facial', label: 'Lifting facial' },
    { value: 'gluteoplastia', label: 'Gluteoplastia' },
    { value: 'braquioplastia', label: 'Braquioplastia (Brazos)' },
    { value: 'otro', label: 'Otro procedimiento' },
  ];

  // Verificar token al cargar
  useEffect(() => {
    const verifyToken = async () => {
      try {
        const response = await fetch(`${API_URL}/api/image-upload/verify/${token}`);
        const data = await response.json();
        setTokenValid(data.valid);
        if (!data.valid) {
          setMessage(data.message || 'Token inválido o expirado');
        }
      } catch (error) {
        setTokenValid(false);
        setMessage('Error verificando token');
      }
    };

    if (token) {
      verifyToken();
    } else {
      setTokenValid(false);
      setMessage('No se proporcionó token');
    }
  }, [token]);

  // Crear preview cuando se selecciona archivo
  useEffect(() => {
    if (!selectedFile) {
      setPreview(null);
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar que sea imagen
      if (!file.type.startsWith('image/')) {
        setStatus('error');
        setMessage('Por favor selecciona una imagen (JPG, PNG, etc.)');
        return;
      }

      // Validar tamaño (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setStatus('error');
        setMessage('La imagen es muy grande. Máximo 10MB.');
        return;
      }

      setSelectedFile(file);
      setStatus('idle');
      setMessage('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    if (!procedimiento) {
      setStatus('error');
      setMessage('Por favor selecciona el tipo de procedimiento');
      return;
    }

    setUploading(true);
    setStatus('uploading');
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('image', selectedFile);
    formData.append('token', token);
    formData.append('procedimiento', procedimiento);
    formData.append('descripcion', descripcion);

    try {
      // Simular progreso mientras sube
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await fetch(`${API_URL}/api/image-upload/analyze`, {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus('success');
        setMessage('¡Imagen enviada correctamente! Revisa tu WhatsApp para ver el análisis.');
      } else {
        setStatus('error');
        setMessage(data.message || 'Error al procesar la imagen');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Error de conexión. Intenta de nuevo.');
    } finally {
      setUploading(false);
    }
  };

  // Pantalla de carga mientras verifica token
  if (tokenValid === null) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <CircularProgress sx={{ color: 'white' }} />
      </Box>
    );
  }

  // Token inválido
  if (tokenValid === false) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: 2,
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            textAlign: 'center',
            maxWidth: 400,
            borderRadius: 3,
          }}
        >
          <ErrorIcon sx={{ fontSize: 60, color: '#ef4444', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Link Inválido
          </Typography>
          <Typography color="text.secondary">
            {message || 'Este enlace ha expirado o no es válido. Por favor solicita un nuevo enlace desde WhatsApp.'}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 2,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            borderRadius: 3,
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <CameraAltIcon sx={{ fontSize: 50, color: '#667eea', mb: 1 }} />
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Subir Imagen para Análisis
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Tu imagen será analizada y recibirás el resultado en WhatsApp
            </Typography>
          </Box>

          {/* Status Success */}
          {status === 'success' && (
            <Alert
              icon={<CheckCircleIcon />}
              severity="success"
              sx={{ mb: 3 }}
            >
              {message}
            </Alert>
          )}

          {/* Status Error */}
          {status === 'error' && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {message}
            </Alert>
          )}

          {/* Upload Area */}
          {status !== 'success' && (
            <>
              <Box
                sx={{
                  border: '2px dashed #667eea',
                  borderRadius: 2,
                  padding: 3,
                  textAlign: 'center',
                  backgroundColor: '#f8f9ff',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  '&:hover': {
                    backgroundColor: '#eef0ff',
                    borderColor: '#5a67d8',
                  },
                  mb: 3,
                }}
                onClick={() => document.getElementById('file-input').click()}
              >
                <input
                  id="file-input"
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />

                {preview ? (
                  <Box>
                    <img
                      src={preview}
                      alt="Preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: 300,
                        borderRadius: 8,
                        marginBottom: 16,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Toca para cambiar la imagen
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <CloudUploadIcon sx={{ fontSize: 60, color: '#667eea', mb: 1 }} />
                    <Typography variant="body1" fontWeight="medium" gutterBottom>
                      Toca para seleccionar o tomar una foto
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      JPG, PNG - Máximo 10MB
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Selector de Procedimiento */}
              <TextField
                select
                fullWidth
                label="Tipo de procedimiento *"
                value={procedimiento}
                onChange={(e) => setProcedimiento(e.target.value)}
                sx={{ mb: 2 }}
                size="small"
              >
                {procedimientos.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              {/* Descripción adicional */}
              <TextField
                fullWidth
                label="Descripción adicional (opcional)"
                placeholder="Ej: Cirugía hace 3 semanas, zona del seno izquierdo..."
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                multiline
                rows={2}
                sx={{ mb: 3 }}
                size="small"
              />

              {/* Progress Bar */}
              {status === 'uploading' && (
                <Box sx={{ mb: 3 }}>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: '#667eea',
                      },
                    }}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1, textAlign: 'center' }}
                  >
                    Analizando imagen... {uploadProgress}%
                  </Typography>
                </Box>
              )}

              {/* Upload Button */}
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleUpload}
                disabled={!selectedFile || !procedimiento || uploading}
                startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                sx={{
                  backgroundColor: '#667eea',
                  '&:hover': {
                    backgroundColor: '#5a67d8',
                  },
                  py: 1.5,
                  fontSize: '1rem',
                }}
              >
                {uploading ? 'Procesando...' : 'Enviar Imagen'}
              </Button>
            </>
          )}

          {/* Footer */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 3 }}
          >
            Tu imagen es procesada de forma segura y no se almacena
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}

export default ImageUpload;
